import { CertificationStatusChipOrIcon, EmbeddableVirtualConnectionDataQualityWarning } from '@tableau/data-catalog-client-components';
import { LabelAssetType } from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { SiteUrlsProps } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { catalogApolloClient } from '~src/catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '~src/catalog/CatalogFeatureFlagsAdapter';
import { useRenderLabelsVpc } from '~src/catalog/CatalogLabelsHookHelper';
import { CERTIFIED_INDICATOR_TYPE, dataQualityIndicatorInfosToProps,} from '~src/catalog/components/labels/DataQualityType';
import { CatalogTelemetryProvider, getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import { dataQualityIndicatorInfoToDataQualityIndicator } from '~src/catalog/models/IDataQualityIndicatorInfo';
import { selectCatalogingOnSiteEnabled } from '~src/catalog/selectors/CatalogSelectors';
import { DataCatalogUIActionsService } from '~src/catalog/services/DataCatalogUIActionsService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { sanitize } from '~src/commonInfra/utils/Sanitize';
import {
  EditPublishedConnectionEntityAction,
} from '~src/publishedConnection/entityActions/EditPublishedConnectionEntityAction';
import { IPublishedConnectionActions } from '~src/publishedConnection/serverApi/types/IPublishedConnectionActions';
import { isPublishedConnectionTableInfo } from '~src/publishedConnectionTable/resources/PublishedConnectionTables';
import { useSelector } from '~src/settings/general-settings/utils';
import { ItemDescription } from '~src/spacesPlatform/components/header/HeaderItemDescription';
import { ModifiedDateMetadata, OwnerMetadata, PublishedConnectionPolicyMetaData, VirtualConnectionExtractStatusMetadata } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader, SpaceHeaderTestIds } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';
import { ISpaceItemDetailInfo } from '~src/spacesPlatform/models/ISpaceItemDetailInfo';
import { TagsMetadata } from '~src/tags/components/header/TagsMetadata';

import { PublishedConnections } from '../resources/PublishedConnections';

export interface PublishedConnectionSpaceHeaderProps extends SiteUrlsProps {
  readonly itemInfo: ISpaceItemDetailInfo<'virtualConnection'>;
  readonly tableExtractStatusLabel?: String;
  readonly availableActions: IPublishedConnectionActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly catalogingOnSiteEnabled: boolean;
}

export function PublishedConnectionSpaceHeader(props: PublishedConnectionSpaceHeaderProps): JSX.Element {
  const tagUrl = React.useCallback((tag: string) => props.urls.virtualConnectionsWithTagUrl(tag), [props.urls]);
  const itemInfo = props.itemInfo as unknown as IContentInfo<Extract<ExplorableItemType, LabelAssetType>>;
  const { warnings, certifications } = useRenderLabelsVpc({ category: 'all', itemInfo: itemInfo, masthead: true, kind: 'chip'});

  return (
    <ConnectedSpaceHeader
      item={props.itemInfo}
      availableActions={{ ...props.availableActions }}
      nameRowIcons={
        <React.Fragment>
          {FavoriteToggleWithCountsIcon({
            item: props.itemInfo,
            addToFavorites: props.addToFavorites,
            removeFromFavorites: props.removeFromFavorites,
          })}
          {ActionsMenuIcon(props.itemInfo)}
        </React.Fragment>
      }
      metadataRow={
        <React.Fragment>
          {OwnerMetadata(props.itemInfo.owner, props.urls)}
          {ModifiedDateMetadata(props.itemInfo.updatedAt)}
          {VirtualConnectionExtractStatusMetadata(props.itemInfo.tables, props.tableExtractStatusLabel)}
          {isPublishedConnectionTableInfo(props.itemInfo) && PublishedConnectionPolicyMetaData(props.itemInfo)}
          {FeatureFlags.labelsInMorePlacesEnabled ?
            <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>{certifications}</span>
            : CertificationMetadata(props.itemInfo, props.urls)}
          {FeatureFlags.labelsInMorePlacesEnabled?
            <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>{warnings}</span>
            : selectCatalogingOnSiteEnabled &&
            <DataQualityMetadata itemInfo={props.itemInfo} urls={props.urls} />}
        </React.Fragment>
      }
      spotlightActions={[EditPublishedConnectionEntityAction]}
      descriptionElement={ItemDescription(props.itemInfo, props.openDetail, sanitize)}
      bottomElement={TagsMetadata({ itemInfo: props.itemInfo, tagUrl })}
    />
  );
}

/** @deprecated TFS1478812 */
function DataQualityMetadata(props: {
  itemInfo: ISpaceItemDetailInfo<'virtualConnection'>;
  urls: SiteUrls;
}): JSX.Element | null {
  const anchoredWarnings = props.itemInfo.dataQualityIndicators.filter(t => t.type !== CERTIFIED_INDICATOR_TYPE);
  const dataQualityWarningsOnThisItem = dataQualityIndicatorInfosToProps(
    {
      assetUrl: props.urls.virtualConnectionUrl(props.itemInfo.id),
      assetName: props.itemInfo.name,
      assetType: 'VirtualConnection',
      onContentItem: true
    },
    props.itemInfo.dataQualityIndicators
  );
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);

  return (
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
      <EmbeddableVirtualConnectionDataQualityWarning
        client={catalogApolloClient}
        vizportalUrlId={props.itemInfo.id}
        ownContentWarnings={dataQualityWarningsOnThisItem}
        siteUrls={props.urls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        featureFlags={catalogFeatureFlagsAdapter()}
        asset={{
          assetName: props.itemInfo.name,
          assetType: 'VirtualConnection',
          assetUrl: urls => urls.virtualConnectionUrl(props.itemInfo.id)
        }}
        anchoredWarnings={anchoredWarnings.map(i => dataQualityIndicatorInfoToDataQualityIndicator(i))}
        telemetryContext={getTelemetryCommonProperties()}
        source={'PublishedConnectionSpaceHeader'}
        siteLuid={siteLuid}
      />
    </span>
  );
}

/** @deprecated TFS1478812 */
function CertificationMetadata(
  itemInfo: ISpaceItemDetailInfo<'virtualConnection'>,
  urls: SpacesSiteUrls
): JSX.Element {
  const isCertified = PublishedConnections.hasCertification(itemInfo);
  const indicators = itemInfo.dataQualityIndicators.filter(i => i.type === CERTIFIED_INDICATOR_TYPE);
  const indicator = indicators[0];

  return isCertified &&
    <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin} data-tb-test-id={SpaceHeaderTestIds.certifiedBy}>
      <CatalogTelemetryProvider>
        <CertificationStatusChipOrIcon
          subjectArea={'VirtualConnection'}
          kind={'vizportal'}
          anchoredAsset={{
            assetName: itemInfo.name,
            assetType: 'VirtualConnection',
            assetUrl: urls => urls.virtualConnectionUrl(itemInfo.id)
          }}
          anchoredCertifications={[dataQualityIndicatorInfoToDataQualityIndicator(indicator)]}
          iconOnly={false}
          urls={urls}
          source={'PublishedConnectionSpaceHeader'}
        />
      </CatalogTelemetryProvider>
    </span>;
}
